import { useEditor } from '../../state/store';
import { ColorMode, colorToHex, hexToColor } from '../../core/types';
import { Checkbox, Field, NumberInput, Panel, Segmented } from '../widgets/common';

const MODES: ReadonlyArray<{ value: ColorMode; label: string }> = [
  { value: 'ansi16', label: '16' },
  { value: 'ansi256', label: '256' },
  { value: 'truecolor', label: 'True' },
];

/** Document settings plus the view options that affect the canvas overlay. */
export function DocumentPanel(): JSX.Element {
  const revision = useEditor((s) => s.revision);
  const render = useEditor((s) => s.renderOptions);
  const store = useEditor.getState();
  const tab = store.active();
  const doc = tab.doc;

  return (
    <Panel title="Document" defaultOpen={false}>
      <div key={revision}>
        <Field label="Name">
          <input className="input" value={doc.name} onChange={(e) => store.renameDocument(e.target.value)} />
        </Field>
        <Field label="Size">
          <div className="row">
            <NumberInput
              value={doc.width}
              min={1}
              max={2000}
              onChange={(v) => store.resizeDocument(v, doc.height)}
            />
            <span className="faint">×</span>
            <NumberInput
              value={doc.height}
              min={1}
              max={2000}
              onChange={(v) => store.resizeDocument(doc.width, v)}
            />
          </div>
        </Field>
        <Field label="Colour mode">
          <Segmented
            value={doc.colorMode}
            options={MODES.map((m) => ({ value: m.value, label: m.label }))}
            onChange={(v) => store.setColorMode(v)}
          />
        </Field>
        <Field label="Background">
          <div className="row">
            <input
              type="color"
              value={colorToHex(doc.background)}
              style={{ width: 34, height: 26, padding: 0, border: '1px solid var(--border)', borderRadius: 4 }}
              onChange={(e) => store.setBackground(hexToColor(e.target.value))}
            />
            <input
              className="input mono"
              value={colorToHex(doc.background)}
              onChange={(e) => store.setBackground(hexToColor(e.target.value))}
            />
          </div>
        </Field>
      </div>

      <div className="faint" style={{ margin: '10px 0 4px' }}>
        View
      </div>
      <Field label="Preview as">
        <Segmented
          value={render.previewMode}
          options={MODES.map((m) => ({ value: m.value, label: m.label }))}
          onChange={(v) => store.setRenderOptions({ previewMode: v })}
        />
      </Field>
      <Field label="Font size">
        <NumberInput
          value={render.fontSize}
          min={8}
          max={40}
          onChange={(v) => store.setRenderOptions({ fontSize: v })}
        />
      </Field>
      <Checkbox label="Grid" checked={render.showGrid} onChange={(v) => store.setRenderOptions({ showGrid: v })} />
      <Checkbox
        label="Rulers"
        checked={render.showRulers}
        onChange={(v) => store.setRenderOptions({ showRulers: v })}
      />
      <Checkbox
        label="Guides"
        checked={render.showGuides}
        onChange={(v) => store.setRenderOptions({ showGuides: v })}
      />
      <Checkbox
        label="Dim inactive layers"
        checked={render.isolateActiveLayer}
        onChange={(v) => store.setRenderOptions({ isolateActiveLayer: v })}
      />

      <div className="faint" style={{ margin: '10px 0 4px' }}>
        Safe areas
      </div>
      {render.safeAreas.map((area, i) => (
        <Checkbox
          key={area.label}
          label={area.label}
          checked={area.enabled}
          onChange={(v) => {
            const next = render.safeAreas.map((a, j) => (i === j ? { ...a, enabled: v } : a));
            store.setRenderOptions({ safeAreas: next });
          }}
        />
      ))}
    </Panel>
  );
}
